import React, { useEffect, useState, useRef, useContext, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import _ from "lodash";
import cx from "classnames";
import { Dropdown, Button, Form, Modal } from "react-bootstrap";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import dateformat from "dateformat";

import CustomFieldItem from "./components/customFieldItem";
import { ListFieldOption } from "./components/listField/options";
import { guid } from "services/util";
import { Sortable } from "@shopify/draggable";
import { toast } from "react-toastify";

import { FieldsContext } from "pages/storyboards/storyboardSettings/customFields";
import { FormFieldsContext } from "pages/settings/customFormFields";
import storyboardRoleService from "services/storyboardRoles.service";
import { FailureToast, SuccessToast } from "components/Toast";
import { invalidateCustomFormFields } from "actions/formFields";

import styles from "./fields.module.scss";
import { MIXPANEL_FORM_FIELDS_CREATED } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { getCustomFieldType } from "services/mixpanelUtil";
import InfiniteScroll from "components/InfiniteScroll";

export const FieldsView = ({ loading, creating, updating, storyboardId, workspaceId, customFields, currentField, entityType, hideButtons }) => {

  const CustomFieldsContext = entityType == "ASSET" ? FieldsContext : FormFieldsContext;
  const { path, fetchCustomFields, fetchingCustomField, fetchingPaperfliteCustomFields, fetchingPaperfliteCustomFieldsSync, createCustomFieldFromPf, createCustomField, updateCustomField, deleteCustomField, invalidateCustomFields } = useContext(CustomFieldsContext);

  const customFieldReferences = customFields.map(field => field.reference);
  const paperfliteCustomFields = useSelector((state) => _.get(state, "fields.paperfliteFields") || []);
  const paperfliteCustomFieldsLoading = useSelector((state) => _.get(state, "fields.paperfliteLoading") || false)
  const error = useSelector((state) => _.get(state, "fields.error", null));

  const paperfliteCustomFieldsSync = useSelector((state) => _.get(state, "fields.paperfliteSync") || []);
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  const dispatch = useDispatch();
  const history = useHistory();
  const locationData = useLocation()
  const [deletingId, setDeletingId] = useState(null);
  const [canCreate, setCanCreate] = useState(false);
  const [currentFieldType, setCurrentFieldType] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('All Fields');
  const [show, setShow] = useState(false);
  const [selectedFields, setSelectedFields] = useState('');
  const [order, setOrder] = useState('');
  const [filteredCustomFields, setFilteredCustomFields] = useState([]);

  const [statePopUp, setStatePopUp] = useState({
    popUp: false,
  })

  const [state, setState] = useState({
    selection: {}
  });

  const updateFieldType = (fieldType) => {
    setCanCreate(true);
    handleCreateField(fieldType);
  };

  const checkWorkspaceSettings = () => {
    if (!locationData) {
      return false;
    }
    const { pathname } = locationData;
    return _.includes(pathname, 'integration-settings') ? true : false;
  };

  useEffect(() => {
    const id = entityType == "FORM" ? workspaceId : storyboardId;
    dispatch(fetchCustomFields(id, false));

    return () => {
      dispatch(invalidateCustomFields());
    };
  }, []);

  useEffect(() => {
    if (error) {
      let errorDescription = "Unable to update Changes";

      toast(
        <FailureToast
          heading={`Failure`}
          description={errorDescription}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateCustomFormFields());
    }
  }, [error]);

  useEffect(() => {
    const fields = entityType == "FORM" ? customFields.filter(item => item.userDefined) : customFields;
    if (fields && fields.length > 0) {
      if (storyboardId){
        dispatch(fetchingCustomField(storyboardId, _.get(currentField, "id") || fields[0].id));
      }
      setCanCreate(true);
      setFilteredCustomFields(customFields);
    } else {
      setCanCreate(false);
    }
  }, [customFields]);

  useEffect(() => {
    if (currentField) {
      history.push(`${path}${currentField.id}`);
      setCurrentFieldType(currentField.type);
    }

    setState({
      ...state,
      selection: currentField,
      creating: currentField?.source != "PAPERFLITE" 
    });
  }, [currentField]);

  const renderFieldName = (fieldType) => {
    const fields = entityType == "FORM" ? customFields.filter(item => item.userDefined) : customFields;
    let order = _.size([...fields].filter((item) => (_.get(item, "type") || "list_field") === fieldType)) + 1;

    switch (fieldType) {
      case "string_field":
        return `Text ${order}`;
      case "list_field":
        return `List ${order}`;
      case "number_field":
        return `Number ${order}`;
      case "boolean_field":
        return `Decision Box ${order}`;
    }
  };

  const handleClick = (customField) => {
    if (customField && customField !== _.get(state,'selection.id')){
      history.push(`${path}${customField}`);
      dispatch(fetchingCustomField(storyboardId, customField));
    }
  };

  const handleChange = (name, value) => {
    let updatedField = {
      ...state.selection,
      [name]: value,
    };

    setState((state) => {
      return {
        ...state,
        selection: updatedField,
      };
    });
  };

  const handleSave = () => {
    var selection = Object.assign({}, currentField, state.selection);
    dispatch(updateCustomField(storyboardId, currentField.id, selection, () => {

        toast(
          <SuccessToast
            heading={`Success`}
            description={`Changes saved successfully`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
    ));
  };

  const handleResetClick = () => {
    setState((state) => {
      return {
        ...state,
        selection: currentField,
      };
    });
  };

  const handleDelete = (customField) => {
    setDeletingId(customField);
    setTimeout(() => {
      let options = customFields.filter(
        (item) => item.id !== customField
      );
      setFilteredCustomFields(options)
      setDeletingId(null);
    }, 500);
    dispatch(deleteCustomField(storyboardId, customField));
  };

  const addOption = () => {
    let selectedOptions = _.get(state, "selection.options", []);
    let order = _.size(selectedOptions) + 1;
    let item = {
      id: guid(),
      value: `Option ${order}`,
    };

    setState((state) => {
      return {
        ...state,
        selection: {
          ...state.selection,
          options: [...selectedOptions, item],
        },
      };
    });
  };

  const handleDeleteClick = (option) => {
    setDeletingId(option.id);
    setTimeout(() => {
      let options = state.selection.options.filter(
        (item) => item.id !== option.id
      );
      setState((state) => ({
        ...state,
        selection: {
          ...state.selection,
          options: options,
        },
      }));
      setDeletingId(null);
    }, 500);
  };

  const handleCreateField = (fieldType) => {

    let item = {
      name: renderFieldName(fieldType),
      helpText: "",
      placeholder: "",
      multiselect: false,
      type: fieldType,
      entityType: entityType
    };

    if (fieldType == "list_field") {
      item = { 
        ...item, 
        options: [{ id: guid(), value: `Option 1`}], 
        placeholder: "Select" 
      };
    }

    if (fieldType == "boolean_field") {
      item = {
        ...item,
        instructions: "Sample Instructions",
        initialState: true,
      };
    }

    if (fieldType == "number_field" || fieldType == "string_field") {
      item = { 
        ...item, 
        placeholder: "Enter Answer" 
      };
    }

    dispatch(createCustomField(storyboardId, item));

    if(!storyboardId){
      const mixpanelPayload = {
        custom_form_field_type: getCustomFieldType(fieldType)
      }
  
      dispatch(createMixpanelEvent(MIXPANEL_FORM_FIELDS_CREATED, mixpanelPayload))
    }
  };

  const handleOptionInputChange = (e, option) => {
    let index = state.selection.options.findIndex((item) => {
      return item.id === option.id;
    });

    let options = state.selection.options;

    let updatedOption = {
      id: option.id,
      value: e.target.value,
    };

    options[index] = updatedOption;
    setState((state) => {
      return {
        ...state,
        selection: {
          ...state.selection,
          options: [...options],
        },
      };
    });
  };

  const handleAllFields = (filedType) => {
    setSelectedFilter(filedType);
  }

  const handleImport = () => {
    dispatch(fetchingPaperfliteCustomFields());
    setStatePopUp((state) => {
      return {
        ...state,
        popUp: !state.popUp,
      }
    })
  };

  const handleFieldSelection = (id) => {

    if(customFieldReferences.includes(id)){
      return;
    }

    setSelectedFields((prevSelectedFields) => ({
      ...prevSelectedFields,
      [id]: !prevSelectedFields[id]
    }));
  };

  const handleDropDownToggle = () => {
    setShow(!show);
  }

  const handleClosePopup = () => {
    setStatePopUp((state) => {  
      return{
        ...state,
        popUp: !state.popUp,
      }
    })
  };

  const handleDone = () => {
    const selectedPaperfliteFieldIds = paperfliteCustomFields
    .filter(field => !!selectedFields[field.id])
    .map(field => field.id);

    if(selectedPaperfliteFieldIds.length > 0){
      dispatch(createCustomFieldFromPf(storyboardId, selectedPaperfliteFieldIds));
    }


    setSelectedFields([]);
    setCanCreate(true);
      setStatePopUp((state) => {
        return {
          ...state,
          popUp: !state.popUp,
        }
      })
  }

  const handleClickSync = () => {
    dispatch(fetchingPaperfliteCustomFieldsSync({ storyboardId }));
  }

  const applySelectedFilter = (fields) => {
    if (selectedFilter === 'All Fields') return fields;
    const source = selectedFilter.toUpperCase().replace(' FIELDS', '');
    return fields.filter(item => item.source === source);
  };
  const fieldsCondition = applySelectedFilter(customFields);
 
  useEffect(() => {
    if (fieldsCondition?.length === 0 && state?.selection != null) {
      setState({
        selection: null
      });
    }
  }, [fieldsCondition, state.selection]);

  const handleOrderChange = () => {
    setState((prevState) => {
      const isAscending = order !== "DESC";
      const sortedOptions = [...prevState.selection.options].sort((a, b) => isAscending
          ? a.value.localeCompare(b.value, undefined, { numeric: true, caseFirst: "upper" })
          : b.value.localeCompare(a.value, undefined, { numeric: true, caseFirst: "upper" })
      );
      setOrder(isAscending ? "DESC" : "ASC");
  
      return {
        ...prevState,
        selection: { ...prevState.selection, options: sortedOptions },
      };
    });
  };

  useEffect(() => {
    setOrder("");
  },[currentField])

  return (
    <Fragment>
      <>
        {loading && customFields.length == 0 && <LoadingMessage />}

        {!loading && !updating && !canCreate && (
          <CustomFieldsEmptyMessage
            handleCreate={updateFieldType}
            handleImport={handleImport}
            entityType={entityType}
            hideButtons={hideButtons}
          />
        )}

        { canCreate && (
          <>
            <div className={cx(styles.customfieldsContainer, {[styles.formFieldsContainer]: entityType == 'FORM'})}>
              <div className={cx(styles.listView, {[styles.assetListView]: entityType == 'ASSET', [styles.formListView]: entityType == 'FORM'})}>
                {entityType == "FORM" && (
                  <div className={styles.fieldsHeader}>
                    <header>Fields</header>
                      <CreateCustomField
                        onCreate={updateFieldType}
                        onImport={handleImport}
                        entityType={entityType}
                        creating={creating}
                      />
                  </div>
                )}

                <div className={cx(styles.wrapperContainer, {[styles.assetWrap]: entityType == 'ASSET', [styles.formWrap]: entityType == 'FORM'})}>
                  <div className={cx(styles.fieldsListContainer, {[styles.assetListContainer]: entityType == 'ASSET', [styles.formListContainer]: entityType == 'FORM'})}>
                    {entityType == "ASSET" && (
                      <div className={styles.fieldsHeader}>
                        <div className={styles.syncContainer}>
                          <Dropdown className={styles.fieldDropDown}>
                            <Dropdown.Toggle>
                              {selectedFilter}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item active={selectedFilter === 'All Fields'} onClick={() => handleAllFields('All Fields')}>
                                All Fields
                              </Dropdown.Item>
                              <Dropdown.Item active={selectedFilter === 'Cleverstory Fields'} onClick={() => handleAllFields('Cleverstory Fields')}>
                                Cleverstory Fields
                              </Dropdown.Item>
                              <Dropdown.Item active={selectedFilter === 'Paperflite Fields'} onClick={() => handleAllFields('Paperflite Fields')}> 
                                Paperflite Fields
                              </Dropdown.Item>
                              </Dropdown.Menu>
                          </Dropdown>
                          
                          {/* TODO: paperflite custom fields sync handling by mongo trigger  
                          
                          {currentField?.source == "PAPERFLITE" && 
                            <span className={styles.syncdivider}>
                              <button className={styles.syncBtn} onClick={handleClickSync}>
                                <i className={'cleverstory-sync'} />
                              </button>
                            </span>
                          } 

                          */}

                        </div>                        
                        <Dropdown
                          onMouseLeave={() => setShow(false)}
                          onMouseOver={() => setShow(true)}
                          className={cx(styles.createFieldButton, {[styles.disabled] : !checkWorkspaceSettings() && !storyboardRoleService.canEditStoryboard(storyboard)})}
                          open={show}
                          onToggle={handleDropDownToggle}
                        >
                          <Dropdown.Toggle variant="success">
                            <i className={cx(styles.icon, 'cleverstory-plus')} />
                            New Custom Field
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCreateField("list_field")}>
                              <div><i className='cleverstory-list'></i>
                                List
                              </div>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleImport}> 
                              <div><i className="cleverstory-download"></i>
                                Import from Paperflite 
                              </div>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div> 
                    )}

                    
                  {entityType == "ASSET" && 
                    <FieldsContainer
                    state={state}
                    customFields={filteredCustomFields}
                    handleClick={handleClick}
                    handleDelete={handleDelete}
                    deletingId={deletingId}
                    entityType={entityType}
                    selectedFilter={selectedFilter}
                    storyboard={storyboard}
                    checkWorkspaceSettings={checkWorkspaceSettings}
                  />}

                  {entityType == "FORM" && 
                    <FormFieldsContainer
                    state={state}
                    customFields={filteredCustomFields}
                    handleClick={handleClick}
                    handleDelete={handleDelete}
                    deletingId={deletingId}
                    entityType={entityType}
                    selectedFilter={selectedFilter}
                    storyboard={storyboard}
                    checkWorkspaceSettings={checkWorkspaceSettings}
                />}
                   
                  </div>
                  <div className={styles.rightSideContainer}>
                    {entityType == "ASSET" && (
                      <div className={styles.fieldsHeader}>
                        <div className={styles.syncContainer}>
                          <header style={{marginRight:"10px"}}>Configuration</header>

                          {/* TODO: paperflite custom fields sync handling by mongo trigger 
                           
                          {currentField?.source == "PAPERFLITE" && 
                            <span className={styles.syncdivider}>
                              <button className={styles.syncBtn} onClick={handleClickSync}>
                                <i className={'cleverstory-sync'} />
                              </button>
                            </span>
                          }
                          
                          */}
                        </div>
                      </div>
                    )}
                    <div className={cx(styles.rightSideWrapper, {[styles.assetRightSide]: entityType == 'ASSET', [styles.formRightSide]: entityType == 'FORM'}, {[styles.disabled] : !checkWorkspaceSettings() && !storyboardRoleService.canEditStoryboard(storyboard)})}>
                      <FormContainer
                        state={state}
                        currentFieldType={currentFieldType}
                        handleChange={handleChange}
                        entityType={entityType}
                      />

                      <OptionsContainer
                        state={state}
                        currentFieldType={currentFieldType}
                        handleOptionInputChange={handleOptionInputChange}
                        handleChange={handleChange}
                        addOption={addOption}
                        handleOrderChange={handleOrderChange}
                        order={order}                      
                        handleDeleteClick={handleDeleteClick}
                        deletingId={deletingId}
                      />
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          {(storyboardRoleService.canEditStoryboard(storyboard) || checkWorkspaceSettings())&&
            <div className={styles.buttonContainer}>
              <Button
                variant="secondary"
                disabled={(!loading && customFields.length == 0) || creating || updating}
                className={styles.resetButton}
                type="reset"
                onClick={handleResetClick}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                disabled={(!loading && _.isEmpty(customFields)) || creating || updating || (state.selection && state.selection.name == "") || 
                  (currentFieldType == "list_field" && _.size(_.get(state, "selection.options", [])) == 0)
                }
                className={styles.saveButton}
                type="submit"
                onClick={handleSave}
              >
                {updating ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          }
          </>
        )}
      </>
        <Modal contentClassName={styles.importFields} show={statePopUp.popUp}>
          <Modal.Header className={styles.header}>
            <div className={styles.header}> Paperflite Custom Fields</div>
            <Button variant='info' onClick={handleClosePopup} className={styles.closeButton}>
              <i className={cx('cleverstory-close', styles.closeButtonIcon)} />
            </Button>
          </Modal.Header>

          <Modal.Body className={styles.fieldContainer}>
            {paperfliteCustomFieldsLoading ? (
          <p>Loading custom fields...</p> 
            ) : !paperfliteCustomFieldsLoading && paperfliteCustomFields.length > 0  ? (
              paperfliteCustomFields.map(field => (
                <div key={field.id} className={styles.fieldRow}>
                  <Form.Group className={styles.checkboxLabel}  onClick={() => handleFieldSelection(field.id)}>
                  <Form.Check
                    key={field.id}
                    type="checkbox"
                    checked={!!selectedFields[field.id] || customFieldReferences.includes(field.id)}
                    className={styles.customFieldCheckbox}
                    disabled={!selectedFields[field.id]}
                    />
                      <TruncatedTextWithTooltip className={styles.label} content={field.name} maxWidth={150} placement='top'/>
                    </Form.Group>
                  <div className={styles.updateDate} >Updated: <span>{dateformat(field ? field.modifiedDate : '-', 'dd mmm yyyy')}</span></div>
                </div>
              ))
            ) : (
              <p>No custom fields found in Paperflite.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClosePopup}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleDone}>
              Done
            </Button>
          </Modal.Footer>
        </Modal>
    </Fragment>
  );
};


const FormFieldsContainer = ({ state, customFields, handleClick, handleDelete, deletingId, entityType, selectedFilter, storyboard, checkWorkspaceSettings }) => {
  const dispatch = useDispatch();
  const { fetchCustomFields } = useContext(FormFieldsContext);
  const currentField = useSelector((state) => _.get(state, "formFields.currentField") || null);
  const loading = useSelector((state) => _.get(state, "formFields.loading") || false);
  const pagination = useSelector((state) => _.get(state, "formFields.pagination") || {});
  const workspaceId = useSelector((state) => _.get(state, "workspaces.workspace.item.id") || null);
  const customFormFields = useSelector((state) => _.get(state, "formFields.items", null));
  const hasMoreItems = pagination.currentPage < pagination.totalPages - 1;
  const error = useSelector((state) => _.get(state, "formFields.error") || false);

  const listContainerRef = useRef(null);

  const fields = customFormFields.filter(item => item.userDefined);

  useEffect(() => {
    if(fields && fields.length > 0){
      handleClick(fields[0]?.id)
    }
  }, [selectedFilter])

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages || loading) {
      return;
    }

    if(!error) {
      dispatch(fetchCustomFields(workspaceId, false, nextPage));
    }
  };

  return (
    <div className={cx(styles.fieldsList, {[styles.assetFieldList]: entityType == 'ASSET', [styles.formFieldList]: entityType == 'FORM'})} ref={listContainerRef}>
      <InfiniteScroll
        className={styles.scrollable}
        loading={loading}
        hasMore={hasMoreItems}
        hideLoadMoreButton={true}
        loadMore={() => {
          loadMore();
        }}
      >
        {(fields || []).map((customField) => {
          return (
            <CustomFieldItem
              key={customField.id}
              customField={customField}
              onDelete={() => handleDelete(customField.id)}
              onClick={() => handleClick(customField.id)}
              fieldSelected={customField && state.selection && customField.id === state.selection.id}
              deletingId={deletingId}
              storyboard={storyboard}
              checkWorkspaceSettings={checkWorkspaceSettings}
            />
          );
        })}
      </InfiniteScroll>
    </div>
  );
}

const FieldsContainer = ({ state, customFields, handleClick, handleDelete, deletingId, entityType, selectedFilter, storyboard, checkWorkspaceSettings }) => {
  const currentField = useSelector((state) => _.get(state, "fields.currentField") || null);

  const CustomFieldsContext = entityType == "ASSET" ? FieldsContext : FormFieldsContext;

  const listContainerRef = useRef(null);
  const prevCustomFieldsRef = useRef(customFields.length);

  const applySelectedFilter = (fields) => {
    if (selectedFilter === 'All Fields') return fields;
    const source = selectedFilter.toUpperCase().replace(' FIELDS', '');
    return fields.filter(item => item.source === source);
  };

  const fields = entityType == "FORM" ? (customFields.filter(item => item.userDefined)) : applySelectedFilter(customFields);

  useEffect(() => {
    if(fields && fields.length > 0){
      handleClick(fields[0]?.id)
    }
  }, [selectedFilter])
  
  useEffect(() => {
    if (customFields.length > prevCustomFieldsRef.current && listContainerRef.current) {
      listContainerRef.current.scrollTo({ top: listContainerRef.current.scrollHeight, behavior: 'smooth' });
    }
    prevCustomFieldsRef.current = customFields.length;
  }, [customFields]);

  return (
    <div className={cx(styles.fieldsList, {[styles.assetFieldList]: entityType == 'ASSET', [styles.formFieldList]: entityType == 'FORM'})} ref={listContainerRef}>
      {(fields || []).map((customField) => {
          return (
            <CustomFieldItem
              customField={customField}
              onDelete={() => handleDelete(customField.id)}
              onClick={() => handleClick(customField.id)}
              fieldSelected={customField && state.selection && customField.id === state.selection.id}
              deletingId={deletingId}
              storyboard={storyboard}
              checkWorkspaceSettings={checkWorkspaceSettings}
            />
          );
        })}
    </div>
  );
}

const FormContainer = ({ state, currentFieldType, handleChange, entityType }) => {
  return (
    <div className={styles.formContainer}>
      <Form className={styles.form} onSubmit={(e) => e.preventDefault()}>
        <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            value={state?.selection?.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            autoComplete="off"
            disabled={state.selection && state.selection.source === "PAPERFLITE"}
          />
        </Form.Group>
        <Form.Group controlId="pageDescription" className={styles.textArea}>
          <Form.Label className={styles.label}>Help Text</Form.Label>
          <Form.Control
            as="textarea"
            className={styles.control}
            type="text"
            value={state.selection ? state.selection.helpText : null}
            placeholder="Enter help text"
            onChange={(e) => handleChange("helpText", e.target.value)}
            rows={4}
            disabled={state.selection && state.selection.source === "PAPERFLITE"}
          />
        </Form.Group>

        {entityType == "ASSET" && (
          <div className={styles.multiSelect}>
            <Form.Label className={styles.label}>
              Allow Multi Select Text
            </Form.Label>
            <Form.Check
              type="switch"
              checked={state.selection ? state.selection.multiselect : null}
              className={styles.toggleSwitch}
              onChange={(e) => handleChange("multiselect", e.target.checked)}
              disabled={state.selection && state.selection.source === "PAPERFLITE"}
            />
          </div>
        )}
        {entityType == "FORM" && currentFieldType == "list_field" && (
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>Placeholder Text</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Placeholder text"
              value={state.selection ? state.selection.placeholder : null}
              onChange={(e) => handleChange("placeholder", e.target.value)}
              autoComplete="off"
            />
          </Form.Group>
        )}
      </Form>
    </div>
  );
};

const OptionsContainer = ({ state, currentFieldType, handleChange, handleOptionInputChange, handleOrderChange, order, addOption, handleDeleteClick, deletingId }) => {
  const sortable = useRef(null);
  const optionContainerRef = useRef(null);
  
  useEffect(() => {
    if (!optionContainerRef.current) {
      return;
    }

    sortable.current = new Sortable(optionContainerRef.current, {
      draggable: `.cs-draggable-item`,
      handle: `.cs-drag`,
      delay: 0,
      mirror: {
        appendTo: `.${styles.fieldOptions}`,
        constrainDimensions: true,
      },
    });
  }, [currentFieldType])

  useEffect(() => {
    if (!optionContainerRef.current || !sortable.current) {
      return;
    }

    sortable.current.on("sortable:stop", handleOptionReorder);

    return () => {
      sortable.current.off("sortable:stop", handleOptionReorder);
    };
  }, [state.selection]);

  const handleOptionReorder = (e) => {
    const oldIndex = e.data.oldIndex;
    const newIndex = e.data.newIndex;

    var option = state.selection.options[oldIndex];

    state.selection.options.splice(oldIndex, 1);
    state.selection.options.splice(newIndex, 0, option);
  };

  return (
    <div className={styles.optionsContainer}>
      {currentFieldType == "list_field" && (
        <>
          <div className={styles.optionsHeader}>
            <div className={styles.optionSort}>
              <header>List Options</header>
              { state.selection && state.selection.source != "PAPERFLITE" && 
              <SortOrderRender 
                handleOrderChange={handleOrderChange}
                order={order}
              />
            }
            </div>
            { state.selection && state.selection.source != "PAPERFLITE" && 
              <Button
                variant="primary"
                className={styles.createOptionButton}
                onClick={addOption}
              >
                <i className={"cleverstory-plus"} />
                Add Option
              </Button>
            }            
          </div>
          <div className={styles.fieldOptions} ref={optionContainerRef}>
            <ListFieldOption
              selection={state.selection}
              handleDelete={(option) => handleDeleteClick(option)}
              handleChange={(e, option) => handleOptionInputChange(e, option)}
              deletingId={deletingId}
              ref={optionContainerRef}
              disabled={state.selection && state.selection.source == "PAPERFLITE"}
            />
          </div>
        </>
      )}
      {currentFieldType !== "list_field" && (
        <Form className={styles.form} onSubmit={(e) => e.preventDefault()}>
          {(currentFieldType == "number_field" || currentFieldType == "string_field") && (
            <Form.Group className="mb-3">
              <Form.Label>Placeholder Text</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Placeholder text"
                value={state.selection ? state.selection.placeholder : null}
                onChange={(e) => handleChange("placeholder", e.target.value)}
                autoComplete="off"
              />
            </Form.Group>
          )}
          {currentFieldType == "boolean_field" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Instructions</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Instructions"
                  value={state.selection ? state.selection.instructions : null}
                  onChange={(e) => handleChange("instructions", e.target.value)}
                  autoComplete="off"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Initial State</Form.Label>
                <Dropdown className={cx(styles.optionDropdown)}>
                  <Dropdown.Toggle>
                    {state?.selection?.initialState ? 
                      <span><i className="cleverstory-checkbox" /> Checked</span>
                      :
                      <span><i className="cleverstory-unchecked-box" /> Unchecked </span>
                    }
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="min-width-0">
                    <Dropdown.Item active={state?.selection?.initialState} onClick={() => handleChange("initialState", true)} >
                      <span className={styles.name}> <i className="cleverstory-checkbox" /> Checked</span>
                    </Dropdown.Item>
                    <Dropdown.Item active={!state?.selection?.initialState} onClick={() => handleChange("initialState", false)}>
                      <span className={styles.name}><i className="cleverstory-unchecked-box" /> Unchecked</span>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </>
          )}
        </Form>
      )}
    </div>
  );
}

    const SortOrderRender = ({ handleOrderChange, order }) => {

      return (
        <div className={styles.sortActionContainer}>          
          <div className={styles.conditions}>
            <span className={styles.label}>
              <span>Sort by: </span>
              <img 
                onClick={()=> handleOrderChange()}
                className={cx(styles.sortIcon, order === "ASC" ? styles.ascending : styles.descending)}                
                alt="sortIcon" 
                src={order === '' ? '/images/sortInitial.svg' : '/images/sortIcon.svg'} 
                />
              <span>Name</span>
            </span>
          </div>
        </div>
      );
    }

const CreateCustomField = ({ onCreate, onImport, entityType, creating, hideButtons }) => {
  const [show, setShow] = useState(false);

  const handleToggle = () => {
    setShow(!show);
  };

  return (
    !hideButtons && (  
    <Dropdown
      onMouseLeave={() => setShow(false)}
      onMouseOver={() => setShow(true)}
      className={styles.createFieldButton}
      open={show}
      onToggle={handleToggle}
    >
      <Dropdown.Toggle variant="success" id="dropdown-basic" disabled={creating}>
        <i className={cx(styles.icon, "cleverstory-plus")} /> {entityType == "ASSET" ? "New Custom Field" : "New Form Field"}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {entityType == "FORM" && (<Dropdown.Item onClick={() => onCreate("string_field")}><i className={cx('cleverstory-text-layer', styles.textLayerIcon)}></i>Text</Dropdown.Item>)}
        <Dropdown.Item onClick={() => onCreate("list_field")}><i className="cleverstory-list"></i>List</Dropdown.Item>
        {entityType == "ASSET" && <Dropdown.Item onClick={onImport}><i className="cleverstory-list-field"></i>Import from Paperflite</Dropdown.Item>}
        {entityType == "FORM" && (<Dropdown.Item onClick={() => onCreate("number_field")}><i className="cleverstory-hash"></i>Number</Dropdown.Item>)}
        {entityType == "FORM" && (<Dropdown.Item onClick={() => onCreate("boolean_field")}><i className="cleverstory-checkbox"></i>Decision Box</Dropdown.Item>)}
      </Dropdown.Menu>
    </Dropdown>
  )
  );
};

const CustomFieldsEmptyMessage = ({ handleCreate, handleImport, entityType, hideButtons }) => {

  return (
    <div className={styles.emptyFieldsMessageContainer}>
      <div className={styles.emptyMessage}>
        <div className={styles.heading}>Weather forecast</div>
        <div className={styles.description}>Clear Skies and Empty <br /> Fields</div>
        <CreateCustomField onCreate={handleCreate} onImport={handleImport} entityType={entityType} hideButtons={hideButtons} />
      </div>
      <div className={styles.buttonContainer}>
        <Button variant="secondary" className={styles.resetButton} type="reset"> Reset </Button>
        <Button variant="primary" className={styles.saveButton} type="submit"> Save Changes </Button>
      </div>
    </div>
  );
};

const LoadingMessage = () => {
  return (
    <div className={styles.loaderContainer}>
      <label>Just a moon-ute... Scanning for fields...</label>
    </div>
  );
};
